import React, { useState, useEffect, useContext } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Container, Button, Accordion, Table } from 'react-bootstrap';
import {
    FaCalendarAlt,
    FaClock,
    FaBook,
    FaGraduationCap,
    FaSync,
    FaExclamationTriangle,
    FaArrowLeft,
    FaDownload,
    FaPrint,
    FaCalendarCheck,
    FaChartBar,
    FaListAlt,
    FaSchool,
    FaUserGraduate
} from 'react-icons/fa';
import moment from 'moment';
import { getCachedData, setCachedData } from '../utils/apiCache';
import Swal from 'sweetalert2';
import Header from './Header';
import { Link } from 'react-router-dom';
import { all_routes } from './router/all_routes';
import { ProductContext } from "../context/AppContext";

const ExamTimetable = () => {
    const [examData, setExamData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const routes = all_routes;
    const { GetstudentData } = useContext(ProductContext);
    const clsSecId = GetstudentData?.cls_sec.id;
    const sessionId = GetstudentData?.session;
    const className = GetstudentData?.cls_sec.cls?.Classes || "";
    const sectionName = GetstudentData?.cls_sec.section?.section || "";

    const fetchExamData = async () => {
        if (!clsSecId || !sessionId) {
            setError('Class section ID or Session ID not found');
            return;
        }
        const cacheKey = `exam_timetable_${clsSecId}_${sessionId}`;
        const cached = getCachedData(cacheKey);
        if (cached) {
            setExamData(cached);
            setLoading(false);
            setRefreshing(false);
            
        }
        try {
            if (!cached) setLoading(true);
            setError(null);
            const authtoken = localStorage.getItem('token');
            const response = await fetch(
                `https://api.mirai.healthcare/api/exam-paper-map?class_section_id=${clsSecId}&session_id=${sessionId}`,
                {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'x-auth': authtoken
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setExamData(data.responseData[0]);
            setCachedData(cacheKey, data.responseData[0]);
        } catch (err) {
            console.error('Error fetching exam timetable:', err);
            setError(err.message || 'Failed to fetch exam timetable');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchExamData();
    }, [clsSecId, sessionId]);

    // Print Function
    const handlePrint = () => {
        Swal.fire({
            icon: 'success',
            title: 'Print Started',
            text: 'Exam timetable is being prepared for printing.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745',
            timer: 2000,
            showConfirmButton: false
        });
        
        setTimeout(() => {
            window.print();
        }, 500);
    };

    // Download Function
    const handleDownload = () => {
        Swal.fire({
            icon: 'success',
            title: 'Download Started',
            text: 'Exam timetable download has been initiated.',
            confirmButtonText: 'OK',
            confirmButtonColor: '#17a2b8',
            timer: 2000,
            showConfirmButton: false
        });
    };

    // Refresh Function
    const handleRefresh = () => {
        setRefreshing(true);
        fetchExamData();

        setTimeout(() => {
            Swal.fire({
                icon: 'success',
                title: 'Refreshed',
                text: 'Exam timetable has been refreshed successfully.',
                confirmButtonText: 'OK',
                confirmButtonColor: '#17a2b8',
                timer: 2000,
                showConfirmButton: false
            });
        }, 500);
    };

    // Back Function
    const handleBack = () => {
        window.history.back();
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD MMMM YYYY');
    };

    const formatTime = (dateString) => {
        return moment(dateString).format('hh:mm A');
    };

    const getDayName = (dateString) => {
        return moment(dateString).format('dddd');
    };

    const getStatusBadge = (examDate) => {
        const today = moment().startOf('day');
        const examDay = moment(examDate).startOf('day');
        
        if (examDay.isBefore(today)) {
            return <Badge bg="secondary">Completed</Badge>;
        } else if (examDay.isSame(today)) {
            return <Badge bg="success">Today</Badge>;
        } else {
            return <Badge bg="primary">Upcoming</Badge>;
        }
    };

    const StatCard = ({ icon: Icon, title, value, subtitle, color = 'primary' }) => (
        <Card className="stat-card border-0 h-100 shadow-sm">
            <Card.Body className="p-3">
                <div className="d-flex align-items-center">
                    <div className={`icon-container bg-${color} me-3 flex-shrink-0`}>
                        <Icon size={20} className="text-white" />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="text-muted mb-1 small">{title}</h6>
                        <h4 className="fw-bold text-dark mb-0">{value}</h4>
                        {subtitle && <small className="text-muted">{subtitle}</small>}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content">
                    <Header />
                    <Container className="py-5">
                        <div className="text-center py-5">
                            <div className="loading-spinner">
                                <FaGraduationCap size={48} className="text-primary mb-3" />
                                <Spinner animation="border" variant="primary" className="mb-3" />
                                <h5 className="text-dark">Loading Exam Timetable...</h5>
                                {/* <p className="text-muted">Fetching exam schedule...</p> */}
                            </div>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    if (error || !examData) {
        return (
            <div className="page-wrapper">
                <div className="content">
                    <Header />
                    <Container className="py-5">
                        <div className="error-state text-center">
                            <FaExclamationTriangle size={64} className="text-danger mb-3 opacity-50" />
                            <h4 className="text-dark mb-3">Unable to Load Exam Timetable</h4>
                            <p className="text-muted mb-4">{error || 'No exam data available'}</p>
                            <div className="d-flex gap-2 justify-content-center">
                                <Button variant="primary" onClick={fetchExamData}>
                                    <FaSync className="me-2" />
                                    Try Again
                                </Button>
                                <Button variant="outline-secondary" onClick={handleBack}>
                                    <FaArrowLeft className="me-2" />
                                    Go Back
                                </Button>
                            </div>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                <Header />

                <div className="py-4">
                    {/* Modern Header */}
                    <div className="page-header-simple mb-4">
                        <div className="header-content">
                            {/* Breadcrumb */}
                            {/* <nav aria-label="breadcrumb" className="mb-3">
                                <ol className="breadcrumb simple-breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to={routes.adminDashboard} className="text-decoration-none text-muted">
                                            Dashboard
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to={routes.academics} className="text-decoration-none text-muted">
                                            Academics
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-primary">Exam Timetable</li>
                                </ol>
                            </nav> */}

                            {/* Main Header Row */}
                            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-3">
                                {/* Title Section */}
                                <div className="d-flex align-items-center gap-3">
                                    <div className="header-icon-simple">
                                        <FaGraduationCap size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <h2
              className="page-title mb-1 w-bold mb-0"
              style={{ letterSpacing: "0.5px" }}>Exam Timetable</h2>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span className="text-muted small">
                                                <FaSchool className="me-1" size={12} />
                                                Class: {className} - {sectionName}
                                            </span>
                                            <span className="text-muted small">
                                                <FaCalendarAlt className="me-1" size={12} />
                                                Session: {examData.session_name}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="d-flex gap-2 action-buttons">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className="btn-action"
                                        onClick={handlePrint}
                                    >
                                        <FaPrint className="me-1" size={14} />
                                        Print
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="btn-action"
                                        onClick={handleDownload}
                                    >
                                        <FaDownload className="me-1" size={14} />
                                        Download
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="btn-action"
                                        onClick={handleBack}
                                    >
                                        <FaArrowLeft className="me-1" size={14} />
                                        Back
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <Row className="g-3 mb-4">
                        <Col xl={3} md={6}>
                            <StatCard
                                icon={FaListAlt}
                                title="Total Exams"
                                value={examData.exam_types?.length || 0}
                                subtitle="Exam Types"
                                color="primary"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                icon={FaBook}
                                title="Total Papers"
                                // value={examData.exam_types?.reduce((total, exam) => total + exam.papers.length, 0) || 0}
                                value={
    new Set(
        examData?.exam_types?.flatMap(
            exam => (exam?.papers || []).map(
                paper => paper?.subject?.name
            )
        )
    ).size || 0
}
                                subtitle="All Subjects"
                                color="info"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                icon={FaCalendarCheck}
                                title="Session"
                                value={examData.session_name}
                                subtitle="Academic Year"
                                color="success"
                            />
                        </Col>
                        <Col xl={3} md={6}>
                            <StatCard
                                icon={FaUserGraduate}
                                title="Class & Section"
                                value={`${className} - ${sectionName}`}
                                subtitle="Current Class"
                                color="warning"
                            />
                        </Col>
                    </Row>

                    {/* Exam Timetable */}
                    <Card className="modern-card border-0 shadow-sm">
                        <Card.Header className="modern-card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <FaCalendarAlt className="me-3 text-primary" />
                                Exam Schedule
                            </h5>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <FaSync className={`me-1 ${refreshing ? 'fa-spin' : ''}`} />
                                {refreshing ? 'Refreshing...' : 'Refresh'}
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Accordion defaultActiveKey="0" flush>
                                {examData.exam_types?.map((examType, examIndex) => (
                                    <Accordion.Item 
                                        key={examType.exam_type_id} 
                                        eventKey={examIndex.toString()}
                                        className="border-0"
                                    >
                                        <Accordion.Header className="exam-type-header">
                                            <div className="d-flex align-items-center w-100">
                                                <FaGraduationCap className="me-3 text-primary" />
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-0 fw-bold">{examType.exam_type_name}</h6>
                                                    <small className="text-muted">{examType.exam_type_title}</small>
                                                </div>
                                                <Badge bg="light" text="dark" className="me-3">
                                                    {examType.papers.length} Papers
                                                </Badge>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body className="p-0">
                                            <div className="table-responsive">
                                                <Table className="mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th className="ps-4">Subject</th>
                                                            <th>Date & Day</th>
                                                            <th>Max Marks</th>
                                                            <th>Min Marks</th>
                                                            <th>Weightage</th>
                                                            <th>Topics</th>
                                                            <th>Status</th>
                                                            <th>Type</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {/* {examType.papers.map((paper, paperIndex) => ( */}
                                                        {Array.from(
    new Map(
        (examType?.papers || []).map(paper => [
            paper?.subject?.name,
            paper
        ])
    ).values()
).map((paper, paperIndex) => (
                                                            <tr key={paper.paper_id} className={paperIndex % 2 === 0 ? 'table-default' : ''}>
                                                                <td className="ps-4 fw-bold">
                                                                    <FaBook className="me-2 text-muted" size={14} />
                                                                    {paper?.subject?.name}
                                                                </td>
                                                                <td>
                                                                    <div>
                                                                        <div className="fw-bold">{formatDate(paper.exam_date)}</div>
                                                                        <small className="text-muted">{getDayName(paper.exam_date)}</small>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <Badge bg="outline-primary" className="border">
                                                                        {paper.max_marks}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Badge bg="outline-secondary" className="border">
                                                                        {paper.min_marks}
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <Badge bg="light" text="dark">
                                                                        {paper.weightage}%
                                                                    </Badge>
                                                                </td>
                                                                <td>
                                                                    <small className="text-muted">
                                                                        {paper.chapter_topic || 'All Chapters'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    {getStatusBadge(paper.exam_date)}
                                                                </td>
                                                                <td>
                                                                    {paper.is_additional ? (
                                                                        <Badge bg="warning" text="dark">Additional</Badge>
                                                                    ) : (
                                                                        <Badge bg="success">Main</Badge>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Card.Body>
                    </Card>

                    {/* Additional Information */}
                    <Row className="g-4 mt-3">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0">
                                        <FaChartBar className="me-2 text-primary" />
                                        Grading Information
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">Grade Based Subjects:</span>
                                        <Badge bg="info">
                                            {examData.exam_types?.reduce((count, exam) => 
                                                count + exam.papers.filter(p => p.is_grade_input_type).length, 0
                                            )}
                                        </Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted">Marks Based Subjects:</span>
                                        <Badge bg="success">
                                            {examData.exam_types?.reduce((count, exam) => 
                                                count + exam.papers.filter(p => !p.is_grade_input_type).length, 0
                                            )}
                                        </Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted">Additional Papers:</span>
                                        <Badge bg="warning" text="dark">
                                            {examData.exam_types?.reduce((count, exam) => 
                                                count + exam.papers.filter(p => p.is_additional).length, 0
                                            )}
                                        </Badge>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0">
                                        <FaClock className="me-2 text-primary" />
                                        Quick Actions
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-grid gap-2">
                                        <Button variant="outline-primary" size="sm" onClick={handlePrint}>
                                            <FaPrint className="me-2" />
                                            Print Timetable
                                        </Button>
                                        <Button variant="outline-success" size="sm" onClick={handleDownload}>
                                            <FaDownload className="me-2" />
                                            Download PDF
                                        </Button>
                                        <Button variant="outline-info" size="sm" onClick={handleRefresh}>
                                            <FaSync className="me-2" />
                                            Refresh Data
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <style jsx>{`
  .page-header-simple {
    background: #ffffff;
    border: 1px solid #e9ecef;
    padding: 1.5rem;
    border-radius: 8px;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .simple-breadcrumb {
    background: transparent;
    padding: 0;
    margin-bottom: 0;
  }

  .simple-breadcrumb .breadcrumb-item a {
    color: #6c757d !important;
    font-size: 0.875rem;
    text-decoration: none;
  }

  .simple-breadcrumb .breadcrumb-item.active {
    color: #495057 !important;
    font-weight: 500;
  }

  .simple-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
    color: #6c757d;
    content: "/";
    padding: 0 0.5rem;
  }

  .header-icon-simple {
    background: #f8f9fa;
    padding: 0.75rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e9ecef;
  }

  .page-title-simple {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
    color: #212529;
    line-height: 1.2;
  }

  .action-buttons {
    flex-shrink: 0;
  }

  .btn-action {
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .btn-action:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .stat-card {
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  .stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  }

  .icon-container {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modern-card {
    border-radius: 8px;
    overflow: hidden;
  }

  .modern-card-header {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    padding: 1rem 1.5rem;
  }

  .exam-type-header {
    background: #f8f9fa;
    border: none;
    padding: 1rem 1.5rem;
  }

  .exam-type-header:not(.collapsed) {
    background: #e3f2fd;
  }

  .accordion-button:not(.collapsed) {
    background: #e3f2fd;
    color: #212529;
    box-shadow: none;
  }

  .accordion-button:focus {
    box-shadow: none;
    border-color: rgba(0,0,0,.125);
  }

  .table th {
    border-top: none;
    font-weight: 600;
    font-size: 0.875rem;
    text-transform: uppercase;
    color: #6c757d;
  }

  .table td {
    vertical-align: middle;
    padding: 1rem 0.75rem;
  }

  .loading-spinner {
    max-width: 400px;
    margin: 0 auto;
  }

  .error-state {
    max-width: 500px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    .page-header-simple {
      padding: 1.25rem;
      margin-bottom: 1.5rem;
    }

    .page-title-simple {
      font-size: 1.25rem;
    }

    .d-flex.flex-column.flex-md-row {
      gap: 1rem !important;
    }

    .action-buttons {
      width: 100%;
      justify-content: center;
    }

    .btn-action {
      flex: 1;
      min-width: 0;
      padding: 0.5rem 0.75rem;
    }

    .table-responsive {
      font-size: 0.875rem;
    }

    .table th,
    .table td {
      padding: 0.5rem 0.25rem;
    }
  }

  @media (max-width: 576px) {
    .page-header-simple {
      padding: 1rem;
    }

    .page-title-simple {
      font-size: 1.125rem;
    }

    .action-buttons {
      flex-wrap: wrap;
    }

    .btn-action {
      flex: 0 0 calc(50% - 0.25rem);
      margin-bottom: 0.5rem;
    }

    .d-flex.align-items-center.gap-3 {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem !important;
    }

    .header-icon-simple {
      align-self: flex-start;
    }
  }

  @media print {
    .action-buttons,
    .btn-action,
    .modern-card-header .btn {
      display: none !important;
    }
    
    .page-header-simple {
      box-shadow: none !important;
      border: 1px solid #000 !important;
    }
    
    .stat-card {
      break-inside: avoid;
    }
  }
`}</style>
            </div>
        </div>
    );
};

export default ExamTimetable;